import React from 'react';
import {
  Body,
  Button,
  Headline,
  Icon,
  designSystemToken,
} from '@lightricks/react-design-system';
import {raiseFlashMessageError} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {VettableCreator} from '@/types/vettableCreatorsResponse';
import useCreateBrandSafetyVetting from '@/hooks/mutations/brand-safety/use-create-brand-safety-vetting/useCreateBrandSafetyVetting';
import styles from './FlagsEmptyState.module.scss';
import flagsIllustration from './flags-illustration.svg';

const CHECK_ITEMS = [
  'Sensitive or controversial topics',
  'Violence, explicit, or high-risk content',
  'Legal and ethical concerns',
];

interface FlagsEmptyStateProps {
  brandId: string;
  creator: VettableCreator;
  canCreateVetting: boolean;
}

function FlagsEmptyState({
  brandId,
  creator,
  canCreateVetting,
}: FlagsEmptyStateProps) {
  const {mutate: createVetting, isLoading} = useCreateBrandSafetyVetting({
    brandId,
  });

  const handleVetCreator = () => {
    createVetting(
      {creator, brandId, type: 'on_platform'},
      {
        onError: () => {
          raiseFlashMessageError();
        },
      }
    );
  };

  return (
    <div className={styles.card}>
      <div className={styles.illustration}>
        <img
          src={flagsIllustration}
          alt="Flags illustration"
          className={styles.illustrationImg}
        />
      </div>
      <div className={styles.content}>
        <div className={styles.textGroup}>
          <Headline size="lg" color={designSystemToken('semantic.fg.primary')}>
            {translate("This creator hasn't been vetted yet")}
          </Headline>
          <Body size="lg" color={designSystemToken('semantic.fg.primary')}>
            {translate(
              'Vet this creator to uncover flagged content in key risk areas.'
            )}
          </Body>
          <ul className={styles.checklist}>
            {CHECK_ITEMS.map((item) => (
              <li key={item} className={styles.checkItem}>
                <Icon name="Actions-Accept" size="small" appearance="black" />
                <Body
                  size="md"
                  color={designSystemToken('semantic.fg.primary')}
                >
                  {translate(item)}
                </Body>
              </li>
            ))}
          </ul>
        </div>
        {canCreateVetting && (
          <Button
            appearance="brand"
            mode="filled"
            size="medium"
            sx={{
              'backgroundColor': designSystemToken(
                'semantic.fg.brand-secondary'
              ),
              'paddingLeft': '32px',
              'paddingRight': '32px',
              '&:hover': {
                backgroundColor: designSystemToken(
                  'semantic.fg.brand-secondary'
                ),
                opacity: 0.9,
              },
            }}
            onClick={handleVetCreator}
            disabled={isLoading}
            isLoading={isLoading}
          >
            {translate('Vet creator')}
          </Button>
        )}
      </div>
    </div>
  );
}

export default FlagsEmptyState;
